import React from 'react';
import { useTranslation } from 'react-i18next';
import { useMiniESGStore } from '../store/miniESGStore';
import type { EnergySource } from '../store/miniESGStore';
import { HelpTooltip } from './common/HelpTooltip';
import { SECTOR_OPTIONS, REGION_OPTIONS, EMPLOYEE_BAND_OPTIONS } from '../constants/miniEsgMeta';

export function MiniESGForm() {
  const { t } = useTranslation();
  const store = useMiniESGStore();
  const nameMissing = !((store.companyName || '').trim());

  return (
    <div className="space-y-8">
      {/* Company Info */}
      <div className="border border-evat-neon/30 rounded-lg p-6 bg-[#0b0b0b]">
        <h3 className="text-lg font-orbitron text-evat-neon mb-4">{t("miniESG.companyInfo") || "Company Information"}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-neutral-400 mb-2">
              {t("miniESG.companyName") || "Company Name"} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={store.companyName}
              onChange={(e) => store.setCompanyInfo(e.target.value, store.taxId)}
              className="w-full px-4 py-2 bg-black border border-evat-neon/30 rounded text-neutral-100 focus:border-evat-neon focus:outline-none"
              placeholder={t("miniESG.companyNamePlaceholder") || "Example Ltd."}
            />
            {nameMissing && (
              <div className="mt-1 text-xs text-red-400">
                {t('miniESG.validation.companyNameRequired') || 'Company name is required.'}
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm text-neutral-400 mb-2">{t("miniESG.taxId") || "Tax ID"}</label>
            <input
              type="text"
              value={store.taxId}
              onChange={(e) => store.setCompanyInfo(store.companyName, e.target.value)}
              className="w-full px-4 py-2 bg-black border border-evat-neon/30 rounded text-neutral-100 focus:border-evat-neon focus:outline-none"
              placeholder={t("miniESG.taxIdPlaceholder") || "12345678-1-23"}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="block text-sm text-neutral-400 mb-2">
              {t("miniESG.sectorLabel") || "Sector"}
            </label>
            <select
              value={store.sector || ''}
              onChange={(e) => store.setCompanyMetadata({ sector: e.target.value || undefined })}
              className="w-full px-4 py-2 bg-black border border-evat-neon/30 rounded text-neutral-100 focus:border-evat-neon focus:outline-none"
            >
              {SECTOR_OPTIONS.map((option) => (
                <option key={option.value || 'none'} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm text-neutral-400 mb-2">
              {t("miniESG.regionLabel") || "Region"}
            </label>
            <select
              value={store.region || ''}
              onChange={(e) => store.setCompanyMetadata({ region: e.target.value || undefined })}
              className="w-full px-4 py-2 bg-black border border-evat-neon/30 rounded text-neutral-100 focus:border-evat-neon focus:outline-none"
            >
              {REGION_OPTIONS.map((option) => (
                <option key={option.value || 'none'} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm text-neutral-400 mb-2">
              {t("miniESG.employeeBandLabel") || "Employee band"}
            </label>
            <select
              value={store.employeeBand || ''}
              onChange={(e) => store.setCompanyMetadata({ employeeBand: e.target.value || undefined })}
              className="w-full px-4 py-2 bg-black border border-evat-neon/30 rounded text-neutral-100 focus:border-evat-neon focus:outline-none"
            >
              {EMPLOYEE_BAND_OPTIONS.map((option) => (
                <option key={option.value || 'none'} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm text-neutral-400 mb-2">
              {t("miniESG.benchmarkGroupLabel") || "Benchmark group (optional)"}
            </label>
            <input
              type="text"
              value={store.benchmarkGroup || ''}
              onChange={(e) => store.setCompanyMetadata({ benchmarkGroup: e.target.value || undefined })}
              className="w-full px-4 py-2 bg-black border border-evat-neon/30 rounded text-neutral-100 focus:border-evat-neon focus:outline-none"
              placeholder="CEE Green SMEs"
            />
          </div>
        </div>
      </div>

      {/* Environment Block */}
      <div className="border border-green-500/30 rounded-lg p-6 bg-[#0b0b0b]">
        <h3 className="text-lg font-orbitron text-green-400 mb-4">🟦 A) {t("miniESG.environment") || "Environment"}</h3>
        
        <div className="space-y-4">
          <div>
            <label className="flex items-center text-sm text-neutral-400 mb-2">
              <span>1. {t("miniESG.questions.env1") || "Annual electricity consumption (kWh)?"}</span>
              <HelpTooltip text={t("miniESG.questions.env1Desc") || "Enter your company's total annual electricity consumption in kilowatt-hours (kWh). Check your electricity bills or utility statements for this information."} />
            </label>
            <div className="flex items-center gap-2">
            <input
              type="number"
                min="0"
                value={(() => {
                  const kwh = store.answers.env1_energyKWh;
                  if (kwh === undefined || kwh === null) return '';
                  return store.energyUnit === 'MWh' ? (kwh / 1000) : kwh;
                })() as any}
                onChange={(e) => {
                  const raw = e.target.value ? Number(e.target.value) : undefined;
                  if (raw === undefined || raw >= 0) {
                    const kwh = raw === undefined ? undefined : (store.energyUnit === 'MWh' ? raw * 1000 : raw);
                    store.setAnswer('env1_energyKWh', kwh);
                  }
                }}
                className="flex-1 px-4 py-2 bg-black border border-green-500/30 rounded text-neutral-100 focus:border-green-500 focus:outline-none"
                placeholder={store.energyUnit === 'MWh' ? '10' : '10000'}
              />
              <select
                value={store.energyUnit || 'kWh'}
                onChange={(e) => store.setEnergyUnit((e.target.value as 'kWh'|'MWh'))}
                className="px-2 py-2 bg-black border border-green-500/30 rounded text-neutral-100 focus:border-green-500 focus:outline-none text-sm"
                title={t('miniESG.energyUnit') || 'Energy unit'}
              >
                <option value="kWh">{t('miniESG.kWh') || 'kWh'}</option>
                <option value="MWh">{t('miniESG.MWh') || 'MWh'}</option>
              </select>
            </div>
            {store.answers.env1_energyKWh !== undefined && store.answers.env1_energyKWh < 0 && (
              <div className="mt-1 text-xs text-red-400">
                {t('miniESG.validation.energyNonNegative') || 'Value must be 0 or greater.'}
              </div>
            )}
          </div>

          <div>
            <label className="flex items-center text-sm text-neutral-400 mb-2">
              <span>2. {t("miniESG.questions.env2") || "What energy sources do you use?"}</span>
              <HelpTooltip text={t("miniESG.questions.env2Desc") || "Select the primary energy source for your operations. Fully renewable includes solar panels, wind power, or green energy contracts. Partially renewable means a mix of grid and renewable sources."} />
            </label>
            <select
              value={store.answers.env2_energySource || ''}
              onChange={(e) => store.setAnswer('env2_energySource', e.target.value as EnergySource | undefined)}
              className="w-full px-4 py-2 bg-black border border-green-500/30 rounded text-neutral-100 focus:border-green-500 focus:outline-none"
            >
              <option value="">{t("miniESG.selectOption") || "Select..."}</option>
              <option value="100% grid">{t("miniESG.energySource.grid") || "100% grid"}</option>
              <option value="partially renewable">{t("miniESG.energySource.partial") || "Partially renewable"}</option>
              <option value="fully renewable">{t("miniESG.energySource.full") || "Fully renewable"}</option>
            </select>
          </div>

          <div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={store.answers.env3_wasteManagement || false}
                onChange={(e) => store.setAnswer('env3_wasteManagement', e.target.checked)}
                className="w-5 h-5 mt-0.5 border border-green-500/30 rounded bg-black text-green-500 focus:ring-green-500 flex-shrink-0"
              />
              <span className="flex-1 flex items-start text-sm text-neutral-300">
                <span>3. {t("miniESG.questions.env3") || "Is there measurable waste management/separation?"}</span>
                <HelpTooltip text={t("miniESG.questions.env3Desc") || "Select if you have a waste separation system (recycling bins, compost, hazardous waste collection). Measurable means you track the amounts of different waste types."} />
              </span>
            </label>
          </div>

          <div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={store.answers.env4_ecoMaterials || false}
                onChange={(e) => store.setAnswer('env4_ecoMaterials', e.target.checked)}
                className="w-5 h-5 mt-0.5 border border-green-500/30 rounded bg-black text-green-500 focus:ring-green-500 flex-shrink-0"
              />
              <span className="flex-1 flex items-start text-sm text-neutral-300">
                <span>4. {t("miniESG.questions.env4") || "Do you use eco-friendly materials or packaging?"}</span>
                <HelpTooltip text={t("miniESG.questions.env4Desc") || "Select if you use recyclable, biodegradable, or sustainably sourced materials/packaging (e.g., recycled paper, biodegradable plastics, FSC-certified materials)."} />
              </span>
            </label>
          </div>

          <div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={store.answers.env5_co2Data || false}
                onChange={(e) => store.setAnswer('env5_co2Data', e.target.checked)}
                className="w-5 h-5 mt-0.5 border border-green-500/30 rounded bg-black text-green-500 focus:ring-green-500 flex-shrink-0"
              />
              <span className="flex-1 flex items-start text-sm text-neutral-300">
                <span>5. {t("miniESG.questions.env5") || "Is CO₂ emission data available (travel, fleet, gas, heating)?"}</span>
                <HelpTooltip text={t("miniESG.questions.env5Desc") || "Select if you track and document CO₂ emissions from company travel (business trips), vehicle fleet (fuel consumption), natural gas usage, or heating systems."} />
              </span>
            </label>
          </div>

          <div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={store.answers.env6_shortTermGoals || false}
                onChange={(e) => store.setAnswer('env6_shortTermGoals', e.target.checked)}
                className="w-5 h-5 mt-0.5 border border-green-500/30 rounded bg-black text-green-500 focus:ring-green-500 flex-shrink-0"
              />
              <span className="flex-1 flex items-start text-sm text-neutral-300">
                <span>6. {t("miniESG.questions.env6") || "Are there short-term environmental goals (e.g., consumption reduction)?"}</span>
                <HelpTooltip text={t("miniESG.questions.env6Desc") || "Select if you have documented environmental goals for the next 1-2 years (e.g., reduce energy consumption by 10%, increase recycling rate, switch to renewable energy)."} />
              </span>
            </label>
          </div>
        </div>
      </div>

      {/* Social Block */}
      <div className="border border-blue-500/30 rounded-lg p-6 bg-[#0b0b0b]">
        <h3 className="text-lg font-orbitron text-blue-400 mb-4">🟦 B) {t("miniESG.social") || "Social"}</h3>
        
        <div className="space-y-4">
          <div>
            <label className="flex items-center text-sm text-neutral-400 mb-2">
              <span>7. {t("miniESG.questions.social1") || "What is the number of employees?"}</span>
              <HelpTooltip text={t("miniESG.questions.social1Desc") || "Enter the total number of employees (full-time equivalent). Include all permanent, part-time, and contract workers who work regularly for your company."} />
            </label>
            <input
              type="number"
              min="1"
              value={store.answers.social1_employeeCount || ''}
              onChange={(e) => {
                const val = e.target.value ? Number(e.target.value) : undefined;
                if (val === undefined || val >= 1) {
                  store.setAnswer('social1_employeeCount', val);
                }
              }}
              className="w-full px-4 py-2 bg-black border border-blue-500/30 rounded text-neutral-100 focus:border-blue-500 focus:outline-none"
              placeholder="10"
            />
            {store.answers.social1_employeeCount !== undefined && store.answers.social1_employeeCount < 1 && (
              <div className="mt-1 text-xs text-red-400">
                {t('miniESG.validation.employeesMin1') || 'Employees must be at least 1.'}
              </div>
            )}
          </div>

          <div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={store.answers.social2_workplaceSafety || false}
                onChange={(e) => store.setAnswer('social2_workplaceSafety', e.target.checked)}
                className="w-5 h-5 mt-0.5 border border-blue-500/30 rounded bg-black text-blue-500 focus:ring-blue-500 flex-shrink-0"
              />
              <span className="flex-1 flex items-start text-sm text-neutral-300">
                <span>8. {t("miniESG.questions.social2") || "Is there a workplace safety system?"}</span>
                <HelpTooltip text={t("miniESG.questions.social2Desc") || "Select if you have documented safety procedures, risk assessments, safety training, emergency plans, or comply with workplace safety regulations (e.g., OSHA, national safety standards)."} />
              </span>
            </label>
          </div>

          <div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={store.answers.social3_trainingProgram || false}
                onChange={(e) => store.setAnswer('social3_trainingProgram', e.target.checked)}
                className="w-5 h-5 mt-0.5 border border-blue-500/30 rounded bg-black text-blue-500 focus:ring-blue-500 flex-shrink-0"
              />
              <span className="flex-1 flex items-start text-sm text-neutral-300">
                <span>9. {t("miniESG.questions.social3") || "Is there an internal training program?"}</span>
                <HelpTooltip text={t("miniESG.questions.social3Desc") || "Select if you provide regular training for employees (skills development, onboarding, professional development, certifications). This can be internal workshops or external courses."} />
              </span>
            </label>
          </div>

          <div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={store.answers.social4_equalityPolicy || false}
                onChange={(e) => store.setAnswer('social4_equalityPolicy', e.target.checked)}
                className="w-5 h-5 mt-0.5 border border-blue-500/30 rounded bg-black text-blue-500 focus:ring-blue-500 flex-shrink-0"
              />
              <span className="flex-1 flex items-start text-sm text-neutral-300">
                <span>10. {t("miniESG.questions.social4") || "Is there an equality/anti-discrimination policy?"}</span>
                <HelpTooltip text={t("miniESG.questions.social4Desc") || "Select if you have a written policy promoting equal opportunities, preventing discrimination based on gender, age, race, religion, disability, or other protected characteristics."} />
              </span>
            </label>
          </div>

          <div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={store.answers.social5_socialProjects || false}
                onChange={(e) => store.setAnswer('social5_socialProjects', e.target.checked)}
                className="w-5 h-5 mt-0.5 border border-blue-500/30 rounded bg-black text-blue-500 focus:ring-blue-500 flex-shrink-0"
              />
              <span className="flex-1 flex items-start text-sm text-neutral-300">
                <span>11. {t("miniESG.questions.social5") || "Are there social projects (sponsorship, donations)?"}</span>
                <HelpTooltip text={t("miniESG.questions.social5Desc") || "Select if your company supports social initiatives through financial sponsorship, donations to charities, volunteering programs, or community involvement projects."} />
              </span>
            </label>
          </div>
        </div>
      </div>

      {/* Governance Block */}
      <div className="border border-purple-500/30 rounded-lg p-6 bg-[#0b0b0b]">
        <h3 className="text-lg font-orbitron text-purple-400 mb-4">🟦 C) {t("miniESG.governance") || "Governance"}</h3>
        
        <div className="space-y-4">
          <div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={store.answers.gov1_compliancePolicy || false}
                onChange={(e) => store.setAnswer('gov1_compliancePolicy', e.target.checked)}
                className="w-5 h-5 mt-0.5 border border-purple-500/30 rounded bg-black text-purple-500 focus:ring-purple-500 flex-shrink-0"
              />
              <span className="flex-1 flex items-start text-sm text-neutral-300">
                <span>12. {t("miniESG.questions.gov1") || "Is there a compliance policy?"}</span>
                <HelpTooltip text={t("miniESG.questions.gov1Desc") || "Select if you have a written policy ensuring compliance with applicable laws, regulations, and industry standards relevant to your business operations."} />
              </span>
            </label>
          </div>

          <div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={store.answers.gov2_ethicsCode || false}
                onChange={(e) => store.setAnswer('gov2_ethicsCode', e.target.checked)}
                className="w-5 h-5 mt-0.5 border border-purple-500/30 rounded bg-black text-purple-500 focus:ring-purple-500 flex-shrink-0"
              />
              <span className="flex-1 flex items-start text-sm text-neutral-300">
                <span>13. {t("miniESG.questions.gov2") || "Is there an ethics code?"}</span>
                <HelpTooltip text={t("miniESG.questions.gov2Desc") || "Select if you have a code of conduct or ethics policy that defines expected behavior, ethical principles, and standards for employees and business partners."} />
              </span>
            </label>
          </div>

          <div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={store.answers.gov3_dataSecurity || false}
                onChange={(e) => store.setAnswer('gov3_dataSecurity', e.target.checked)}
                className="w-5 h-5 mt-0.5 border border-purple-500/30 rounded bg-black text-purple-500 focus:ring-purple-500 flex-shrink-0"
              />
              <span className="flex-1 flex items-start text-sm text-neutral-300">
                <span>14. {t("miniESG.questions.gov3") || "Is there a data security system (GDPR basics)?"}</span>
                <HelpTooltip text={t("miniESG.questions.gov3Desc") || "Select if you have data protection measures, privacy policies, data processing procedures, or comply with GDPR (General Data Protection Regulation) requirements."} />
              </span>
            </label>
          </div>

          <div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={store.answers.gov4_sustainabilityReporting || false}
                onChange={(e) => store.setAnswer('gov4_sustainabilityReporting', e.target.checked)}
                className="w-5 h-5 mt-0.5 border border-purple-500/30 rounded bg-black text-purple-500 focus:ring-purple-500 flex-shrink-0"
              />
              <span className="flex-1 flex items-start text-sm text-neutral-300">
                <span>15. {t("miniESG.questions.gov4") || "Does management report annually on sustainability?"}</span>
                <HelpTooltip text={t("miniESG.questions.gov4Desc") || "Select if your company prepares and publishes an annual sustainability report, ESG report, or includes sustainability information in annual reports."} />
              </span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}



